import { AfterContentInit, ContentChild, ContentChildren, Directive, HostListener, Inject, OnDestroy, QueryList } from '@angular/core';
import { MenuFocusTargetDirective } from './menu-focus-target/menu-focus-target.directive';
import { MenuFocusParentDirective } from './menu-focus-parent/menu-focus-parent.directive';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appMenuFocusHandler]',
  exportAs: 'menuFocusHandler'
})
export class MenuFocusHandlerDirective implements AfterContentInit, OnDestroy {
  private readonly onDestroySubject = new Subject<void>();

  @ContentChild(MenuFocusParentDirective)
  private focusParent: MenuFocusParentDirective;
  @ContentChildren(MenuFocusTargetDirective, { descendants: true })
  private focusTargets: QueryList<MenuFocusTargetDirective>;

  isFocusable = false;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
  }

  ngAfterContentInit(): void {
    if (!this.focusParent) {
      throw new Error('Required input parameter "focusParent" missing');
    }

    this.focusParent.enterSubMenu.pipe(
      takeUntil(this.onDestroySubject)
    ).subscribe(() => {
      this.onFocusSubMenu();
    });
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  onFocusSubMenu(): void {
    this.isFocusable = true;
    this.focusFirstItem();
  }

  @HostListener('focusout', ['$event'])
  onDropdownFocusOut(ev: FocusEvent): void {
    if (!this.isFocusStayedWithinDropdownContainer(ev)) {
      this.isFocusable = false;
    }
  }

  @HostListener('keydown.escape', ['$event'])
  @HostListener('keydown.arrowLeft', ['$event'])
  unFocusDropdown(ev: KeyboardEvent): void {
    this.isFocusable = false;

    if (this.isFocusTargetItem(<HTMLElement>ev.target)) {
      ev.stopPropagation();
      this.focusParent.focus();
    }
  }

  focusNextItem(): void {
    this.moveFocusedItemIndexBy(1);
  }

  focusPreviousItem(): void {
    this.moveFocusedItemIndexBy(-1);

  }

  private moveFocusedItemIndexBy(offset: number): void {
    const items = this.focusTargets.toArray();
    const actualIndex = this.getFocusedItemIndex(items);
    const targetIndex = actualIndex < 0 ? 0 : actualIndex + offset;
    const maxIndex = items.length - 1;

    if (targetIndex >= 0 && targetIndex <= maxIndex) {
      items[targetIndex].focus();
    }
  }

  private getFocusedItemIndex(items: MenuFocusTargetDirective[]): number {
    return items.findIndex(item => item.isFocusWithin(this.document.activeElement));
  }

  private isFocusTargetItem(element: Element): boolean {
    return this.focusTargets.some(target => target.elRef.nativeElement.contains(element));
  }

  private focusFirstItem(): void {
    this.focusTargets.first.focus();
  }

  private isFocusStayedWithinDropdownContainer(ev: FocusEvent): boolean {
    const focusedElement = <Element>ev.relatedTarget;
    return this.isFocusable && this.focusTargets.some(target => target.isFocusWithin(focusedElement));
  }
}
